import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Filter, History, Pencil, PieChart, RefreshCw, X } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import {
  fetchAdminBudgetConsumption,
  fetchAdminBudgetConsumptionMeta,
  updateAdminBudgetPeriod,
  type AdminBudgetConsumptionItem,
  type AdminBudgetConsumptionMeta,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { cn } from "@/shared/lib/cn";
import { BudgetRevisionPanel } from "../components/BudgetRevisionPanel";

function formatUsd(value: number | null | undefined) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function statusClass(status: string) {
  switch (status) {
    case "depleted":
      return "bg-pink-soft text-pink";
    case "alert":
      return "bg-yellow-soft text-yellow";
    default:
      return "bg-teal-soft text-green";
  }
}

export function BudgetConsumptionPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.budgetConsumption;
  const { showToast } = useToast();

  const [meta, setMeta] = useState<AdminBudgetConsumptionMeta | null>(null);
  const [items, setItems] = useState<AdminBudgetConsumptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [periodicityFilter, setPeriodicityFilter] = useState("annuel");
  const [periodKey, setPeriodKey] = useState("");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [revisionRow, setRevisionRow] = useState<AdminBudgetConsumptionItem | null>(null);

  const periodOptions = useMemo(() => meta?.period_options ?? [], [meta]);

  const loadMeta = async (periodicity: string) => {
    const data = await fetchAdminBudgetConsumptionMeta({ periodicity });
    setMeta(data);
    setPeriodKey((current) => current || data.period_options[0]?.value || "");
  };

  const loadItems = async () => {
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminBudgetConsumption({
        company_id: companyId ? Number(companyId) : undefined,
        period_key: periodKey || undefined,
      });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadMeta(periodicityFilter);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Meta indisponible.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodicityFilter]);

  useEffect(() => {
    if (!periodKey) return;
    void loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey, companyId]);

  const startEdit = (row: AdminBudgetConsumptionItem) => {
    setEditingKey(`${row.convention_id}:${row.period_key}`);
    setEditValue(String(row.budget ?? ""));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue("");
  };

  const saveEdit = async (row: AdminBudgetConsumptionItem) => {
    const amount = Number(editValue);
    if (!Number.isFinite(amount) || amount < 0) {
      showToast({ message: labels.editInvalid, variant: "warning" });
      return;
    }
    const key = `${row.convention_id}:${row.period_key}`;
    setSavingKey(key);
    try {
      const res = await updateAdminBudgetPeriod(row.convention_id, {
        period_key: row.period_key,
        budget_amount: amount,
      });
      setItems((current) =>
        current.map((item) =>
          item.convention_id === row.convention_id && item.period_key === row.period_key
            ? res.item
            : item,
        ),
      );
      cancelEdit();
      showToast({ message: labels.editSaved, variant: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : labels.editError,
        variant: "warning",
      });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.dashboard, href: "/dashboard" },
          { label: labels.title },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{labels.title}</h1>
          <p className="mt-1 text-sm text-muted">{labels.subtitle}</p>
        </div>
        <Link
          to="/dashboard/conventions"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-card-muted"
        >
          <ArrowLeft size={16} />
          {labels.backToConventions}
        </Link>
      </div>

      <div className="header-ribbon filter-panel relative isolate rounded-[var(--radius-card)] p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Filter size={16} className="text-primary" />
          {labels.filters}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <FormField label={labels.company}>
            <select
              className={selectClass}
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              <option value="">{labels.allCompanies}</option>
              {(meta?.companies ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.periodicity}>
            <select
              className={selectClass}
              value={periodicityFilter}
              onChange={(e) => {
                setPeriodicityFilter(e.target.value);
                setPeriodKey("");
              }}
            >
              {(meta?.periodicities ?? [
                { value: "trimestriel", label: "Trimestriel" },
                { value: "semestriel", label: "Semestriel" },
                { value: "annuel", label: "Annuel" },
              ]).map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.period}>
            <select
              className={selectClass}
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
            >
              {periodOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </FormField>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => void loadItems()}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white"
            >
              <RefreshCw size={16} />
              {labels.refresh}
            </button>
            {companyId && (
              <button
                type="button"
                onClick={() => setCompanyId("")}
                className="inline-flex h-10 items-center rounded-lg border border-border px-3"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted">{labels.hint}</p>
      </div>

      {error && (
        <div className="rounded-lg border border-pink/30 bg-pink-soft/40 px-4 py-3 text-sm text-pink">
          {error}
        </div>
      )}

      <div className="theme-card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold">
          <PieChart size={16} className="text-primary" />
          {labels.tableTitle.replace("{count}", String(items.length))}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-card-muted/40 text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-3">{labels.columns.company}</th>
                <th className="px-4 py-3">{labels.columns.convention}</th>
                <th className="px-4 py-3">{labels.columns.period}</th>
                <th className="px-4 py-3">{labels.columns.budget}</th>
                <th className="px-4 py-3">{labels.columns.forfait}</th>
                <th className="px-4 py-3">{labels.columns.horsForfait}</th>
                <th className="px-4 py-3">{labels.columns.consumed}</th>
                <th className="px-4 py-3">{labels.columns.pct}</th>
                <th className="px-4 py-3">{labels.columns.status}</th>
                <th className="px-4 py-3">{labels.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-muted">
                    {labels.loading}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-muted">
                    {labels.empty}
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const rowKey = `${row.convention_id}:${row.period_key}`;
                  const isEditing = editingKey === rowKey;
                  const isSaving = savingKey === rowKey;

                  return (
                    <tr key={rowKey} className="border-t border-border">
                      <td className="px-4 py-3 font-medium text-foreground">{row.company_name}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{row.convention_name}</div>
                        <div className="text-xs text-muted">{row.convention_code}</div>
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {row.period_key}
                        <div className="text-xs">
                          {row.period_start} → {row.period_end}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex min-w-[160px] items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={cn(inputClass, "h-9")}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              disabled={isSaving}
                              autoFocus
                            />
                            <button
                              type="button"
                              title={labels.saveBudget}
                              disabled={isSaving}
                              onClick={() => void saveEdit(row)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type="button"
                              title={labels.cancelEdit}
                              disabled={isSaving}
                              onClick={cancelEdit}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>{formatUsd(row.budget)}</span>
                            <button
                              type="button"
                              title={labels.editBudget}
                              onClick={() => startEdit(row)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted hover:text-foreground"
                            >
                              <Pencil size={12} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{formatUsd(row.consumed_forfait_mensuel)}</td>
                      <td className="px-4 py-3">{formatUsd(row.consumed_hors_forfait)}</td>
                      <td className="px-4 py-3 font-semibold">{formatUsd(row.consumed)}</td>
                      <td className="px-4 py-3">
                        {row.consumption_pct == null ? "—" : `${row.consumption_pct.toFixed(2)}%`}
                        <div className="mt-1 h-1.5 w-24 overflow-hidden rounded bg-card-muted">
                          <div
                            className={cn(
                              "h-full rounded",
                              (row.consumption_pct ?? 0) >= 100
                                ? "bg-pink"
                                : (row.consumption_pct ?? 0) >= row.alert_threshold_pct
                                  ? "bg-yellow"
                                  : "bg-primary",
                            )}
                            style={{
                              width: `${Math.min(100, Math.max(0, row.consumption_pct ?? 0))}%`,
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase",
                            statusClass(row.status),
                          )}
                        >
                          {labels.status[row.status as keyof typeof labels.status] ?? row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          title={labels.openRevision}
                          onClick={() => setRevisionRow(row)}
                          className="inline-flex h-8 items-center gap-1 rounded-lg border border-border px-2 text-xs font-medium text-foreground hover:bg-card-muted"
                        >
                          <History size={13} />
                          {labels.openRevision}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {revisionRow && (
        <BudgetRevisionPanel
          row={revisionRow}
          labels={labels as unknown as Record<string, string>}
          onClose={() => setRevisionRow(null)}
          onSaved={(item) => {
            setItems((current) =>
              current.map((r) =>
                r.convention_id === item.convention_id && r.period_key === item.period_key ? item : r,
              ),
            );
            showToast({ message: labels.revisionSaved, variant: "success" });
          }}
        />
      )}
    </div>
  );
}
